import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { GuestUserStatsItem } from '../../../../types/dashboard';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../../../shared/GenericTable';
import { GuestRole } from '../../../../enums/users';
import { getReportChipSx } from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

const roleLabel = (role: string) => {
    if (role === GuestRole.Admin) return 'Company Admin';
    if (role === GuestRole.DepartmentAdmin) return 'Team Admin';
    return 'Guest User';
};

export default function GuestUsersReportList({
    data = [],
}: {
    data?: GuestUserStatsItem[];
}) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();

    const columns: MRT_ColumnDef<GuestUserStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'companyName',
                header: 'Company',
                size: 160,
            },
            {
                id: 'name',
                accessorFn: (row) => `${row.firstName} ${row.lastName}`,
                header: 'Name',
                size: 160,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                size: 180,
            },
            {
                id: 'status',
                accessorFn: (row) => (row.isActive ? 'Active' : 'Disabled'),
                header: 'Status',
                size: 150,
                Cell: ({ cell }) => {
                    const isActive = cell.getValue<string>() === 'Active';
                    return (
                        <CustomChip
                            label={
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: theme.palette[
                                            isActive ? 'green' : 'red'
                                        ].main,
                                    }}
                                >
                                    {cell.getValue<string>()}
                                </Typography>
                            }
                            sx={getReportChipSx(
                                isActive ? 'green' : 'red',
                                theme,
                            )}
                            variant='outlined'
                        />
                    );
                },
            },
            {
                id: 'profilePicture',
                accessorFn: (row) =>
                    row.hasProfilePicture ? 'Complete' : 'Incomplete',
                header: 'Profile picture',
                size: 130,
                Cell: ({ cell }) => {
                    const isComplete = cell.getValue<string>() === 'Complete';
                    return (
                        <CustomChip
                            label={
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: theme.palette[
                                            isComplete ? 'green' : 'red'
                                        ].main,
                                    }}
                                >
                                    {cell.getValue<string>()}
                                </Typography>
                            }
                            sx={getReportChipSx(
                                isComplete ? 'green' : 'red',
                                theme,
                            )}
                            variant='outlined'
                        />
                    );
                },
            },
            {
                id: 'role',
                accessorFn: (row) => roleLabel(row.role),
                header: 'Access level',
                size: 130,
            },
            {
                accessorKey: 'lastLoginAt',
                header: 'Last Login',
                size: 120,
                sortingFn: 'datetime',
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>();
                    return value
                        ? moment(value).format('DD/MM/YYYY h:mm a')
                        : '-';
                },
            },
        ],
        [theme],
    );

    if (!data.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual guest users found.
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                width: isMobile ? '90vw' : '100%',
            }}
        >
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={data}
                tableName='Total Guest Users Report'
                paginationItemLabel='users'
            />
        </Box>
    );
}
